import { DS_getData } from '../DS/DS.Data'
import type { FlowReport } from '../../../app-core/lib/flow/FlowReport'
import { IDB_deviceInProgressData } from '../IDB/IDB.deviceDB'
import { IDB_flowReports, IDB_flowAnswers, IDB_openReports } from '../IDB/IDB.dexie'
import { Flow_syncOngoingReports } from '../Flow/Flow.syncOngoingReports'
import { WF_ensureVersionOnDisk } from '../WF/WF.WorkflowReference'
import type { WorkflowReference } from '../../../shared/domain/workflows/types.no-deps'
import { assumeExhaustion } from '../../../shared/lib/typeHelpers.no-deps'
import { type Rpts_OpenReport, Rpts_openReportFetch, Rpts_openReportWrite } from '../Rpts/Rpts.OpenReport'
import { Flow_fetchAnswers } from '../Flow/Flow.Report'
import type { Flow_Answer } from '../Flow/Flow.answer'
import { Logg_createLogger } from '../Logg/Logg.Logger'
import { difference } from 'lodash'

export type Devi_InProgressData =
    | InProgressDataBase<'flowReport', FlowReport<WorkflowReference>>
    | InProgressDataBase<'flowAnswer', Flow_Answer>
    | InProgressDataBase<'activityReport', Rpts_OpenReport>

type InProgressDataBase<T extends string, V> = {
    id?: number // Auto-generated
    userId: number
    customerId: number
    type: T
    value: V
}

/**
 * Called during logout to save in progress data to the device database to be held onto
 * until the user next logs in.
 * This includes flow reports that are in-progress, cancelled or need finalisation
 */
export async function Devi_saveInProgressData() {
    const { user, customer } = DS_getData()

    // Save in-progress flow reports
    const openFlowReports = await IDB_flowReports().where({ status: 'open' }).toArray()

    // Save cancellled flow report that are pending cancel
    const pendingCancelReports = await IDB_flowReports()
        .where({ status: 'cancelled', uploadState: 'pendingCancel' })
        .toArray()

    // Save completed flow report that are pending finalisation
    const pendingFinalisationReports = await IDB_flowReports()
        .where({ status: 'completed', uploadState: 'pendingFinalisation' })
        .toArray()

    // Get flow reports of subtasks for open and pending finalisation reports
    // We don't want to save subtask for cancelled flow reports as they will be cancelled on server
    // when cancel endpoint is called
    const subtasks = await IDB_flowReports()
        .where('progenitorClientId')
        .anyOf([...openFlowReports, ...pendingFinalisationReports].map((r) => r.id))
        .toArray()

    // All flow reports that need to be saved to device db
    let allFlowReports = [...openFlowReports, ...pendingCancelReports, ...pendingFinalisationReports, ...subtasks]

    // Get any unuploaded answer
    const answers = await IDB_flowAnswers().where({ uploadState: 'pendingUpload' }).toArray()

    // It's possible to get into a situation where although the report is not in a pending state
    // It will be once the flow answers for that report are uploaded
    // So here we make sure that any report that has answers to upload, also gets saved for later
    // It's unfortunate that the report lifecycle is so complicated....
    const reportsWithUnuploadedAnswers = await IDB_flowReports()
        .where('id')
        .anyOf(
            difference(
                answers.map((a) => a.report),
                allFlowReports.map((r) => r.id)
            )
        )
        .toArray()

    allFlowReports = [...allFlowReports, ...reportsWithUnuploadedAnswers]

    await IDB_deviceInProgressData().bulkPut(
        allFlowReports.map((flowReport) => ({
            userId: user.id,
            customerId: customer.id,
            type: 'flowReport',
            value: flowReport,
        }))
    )

    await IDB_deviceInProgressData().bulkPut(
        answers.map((answer) => ({
            userId: user.id,
            customerId: customer.id,
            type: 'flowAnswer',
            value: answer,
        }))
    )

    // Save in-progress activity report
    const activityReport = await Rpts_openReportFetch()
    if (activityReport !== undefined) {
        const activityReportFlowAnswers = await Flow_fetchAnswers(activityReport.clientId)

        await IDB_deviceInProgressData().bulkPut([
            {
                userId: user.id,
                customerId: customer.id,
                type: 'activityReport',
                value: activityReport,
            },
        ])

        await IDB_deviceInProgressData().bulkPut(
            activityReportFlowAnswers.map((answer) => ({
                userId: user.id,
                customerId: customer.id,
                type: 'flowAnswer',
                value: answer,
            }))
        )

        // Remove open activity report and answers
        await IDB_openReports().bulkDelete([activityReport.clientId])
        await IDB_flowAnswers().bulkDelete(activityReportFlowAnswers.map((r) => r.id))
    }

    // Remove open flow reports and answers
    await IDB_flowReports().bulkDelete(allFlowReports.map((r) => r.id))
    await IDB_flowAnswers().bulkDelete(answers.map((r) => r.id))
}

/**
 * Called during login to recover in progress data for the current user
 */
export async function Devi_recoverInProgressData(customerId: number, userId: number) {
    const logger = Logg_createLogger(`Devi_recoverInProgressData`)

    const rows = await IDB_deviceInProgressData().where({ customerId, userId }).toArray()

    for (const row of rows) {
        try {
            switch (row.type) {
                case 'flowReport':
                    await IDB_flowReports().put(row.value)
                    await WF_ensureVersionOnDisk(logger, row.value.workflow)
                    await IDB_deviceInProgressData().delete(row.id)
                    break
                case 'flowAnswer':
                    await IDB_flowAnswers().put(row.value)
                    await IDB_deviceInProgressData().delete(row.id)
                    break
                case 'activityReport':
                    await Rpts_openReportWrite(row.value)
                    await IDB_deviceInProgressData().delete(row.id)
                    break

                default:
                    assumeExhaustion(row)
            }
        } catch (error) {
            logger.error(error)
        }
    }

    // Try to sync any flow data straight away
    if (rows.some((r) => r.type === 'flowReport' || r.type === 'flowAnswer')) {
        await Flow_syncOngoingReports(logger)
    }
}
